import type { MaskItem, Skeleton } from 'src/app/controls/anatomy/anatomy.component';
import { PartFindingAnatomyRegionType } from 'src/app/api/proxy/practice/models/part-finding-anatomy-region-type';
import {
  findPartInSkeleton,
  normalizeSkeletonMaskId,
  type SkeletonPartLookup,
} from './anatomy-region.util';

/** Display labels for anatomy note regions (mirrors IQConnect-Admin). */
export const NOTE_REGION_OPTIONS = [
  'Head',
  'Cervical',
  'Upper Extremity',
  'Thoracic',
  'Lumbar',
  'General Torso',
  'Sacral',
  'Pelvic',
  'Lower Extremity',
] as const;

export type NoteRegion = (typeof NOTE_REGION_OPTIONS)[number];

const NOTE_REGION_TO_API: Record<string, PartFindingAnatomyRegionType> = {
  Head: PartFindingAnatomyRegionType.Head,
  Cervical: PartFindingAnatomyRegionType.Cervical,
  'Upper Extremity': PartFindingAnatomyRegionType.UpperExtremity,
  Thoracic: PartFindingAnatomyRegionType.Thoracic,
  Lumbar: PartFindingAnatomyRegionType.Lumbar,
  'General Torso': PartFindingAnatomyRegionType.GeneralTorso,
  Sacral: PartFindingAnatomyRegionType.Sacral,
  Pelvic: PartFindingAnatomyRegionType.Pelvic,
  'Lower Extremity': PartFindingAnatomyRegionType.LowerExtremity,
};

const API_TO_NOTE_REGION: Record<PartFindingAnatomyRegionType, NoteRegion> = {
  [PartFindingAnatomyRegionType.Head]: 'Head',
  [PartFindingAnatomyRegionType.Cervical]: 'Cervical',
  [PartFindingAnatomyRegionType.UpperExtremity]: 'Upper Extremity',
  [PartFindingAnatomyRegionType.Thoracic]: 'Thoracic',
  [PartFindingAnatomyRegionType.Lumbar]: 'Lumbar',
  [PartFindingAnatomyRegionType.GeneralTorso]: 'General Torso',
  [PartFindingAnatomyRegionType.Sacral]: 'Sacral',
  [PartFindingAnatomyRegionType.Pelvic]: 'Pelvic',
  [PartFindingAnatomyRegionType.LowerExtremity]: 'Lower Extremity',
};

/** Sort order aligned with PartFindingAnatomyRegionType enum. */
export const NOTE_REGION_SORT_ORDER: Record<string, number> = {
  Head: 0,
  Cervical: 1,
  'Upper Extremity': 2,
  Thoracic: 3,
  Lumbar: 4,
  'General Torso': 5,
  Sacral: 6,
  Pelvic: 7,
  'Lower Extremity': 8,
};

export function noteRegionToPartFindingAnatomyRegion(
  noteRegion: string,
): PartFindingAnatomyRegionType | null {
  const trimmed = noteRegion?.trim();
  if (!trimmed) {
    return null;
  }
  return NOTE_REGION_TO_API[trimmed] ?? null;
}

export function partFindingAnatomyRegionToNoteRegion(
  region: PartFindingAnatomyRegionType | null | undefined,
): NoteRegion | null {
  if (!region) {
    return null;
  }
  return API_TO_NOTE_REGION[region] ?? null;
}

export function partFindingAnatomyRegionLabel(
  region: PartFindingAnatomyRegionType | null | undefined,
): string {
  return partFindingAnatomyRegionToNoteRegion(region) ?? '';
}

/** Infer note region from vertebral prefix or extremity skeleton regionId when mask has no noteRegion. */
export function inferNoteRegionFromPart(
  partName: string,
  skeletonLookup: Pick<SkeletonPartLookup, 'regionId'> | null | undefined,
): NoteRegion | null {
  const regionId = skeletonLookup?.regionId;
  if (regionId === 18 || regionId === 21) {
    return 'Upper Extremity';
  }
  if (regionId === 4 || regionId === 7) {
    return 'Lower Extremity';
  }

  const name = (partName || '').trim().toUpperCase();
  if (/^C\d/.test(name)) {
    return 'Cervical';
  }
  if (/^T\d/.test(name)) {
    return 'Thoracic';
  }
  if (/^L\d/.test(name)) {
    return 'Lumbar';
  }
  if (/^S\d/.test(name)) {
    return 'Sacral';
  }
  if (/\bCERVICAL\b/.test(name)) {
    return 'Cervical';
  }
  if (/\bTHORACIC\b/.test(name)) {
    return 'Thoracic';
  }
  if (/\bLUMBAR\b/.test(name)) {
    return 'Lumbar';
  }
  if (/\bSACRAL\b/.test(name)) {
    return 'Sacral';
  }
  if (/\bPELVIC\b/.test(name) || /\bPELVIS\b/.test(name)) {
    return 'Pelvic';
  }

  return null;
}

/** Infer note region from skeleton mask asset paths when part names are ambiguous. */
export function inferNoteRegionFromMaskFileName(maskFileName?: string | null): NoteRegion | null {
  const path = (maskFileName || '').trim().toLowerCase();
  if (!path) {
    return null;
  }

  if (path.includes('cervical')) {
    return 'Cervical';
  }
  if (path.includes('thoracic')) {
    return 'Thoracic';
  }
  if (path.includes('lumbar')) {
    return 'Lumbar';
  }
  if (path.includes('sacral') || path.includes('sacrum')) {
    return 'Sacral';
  }
  if (path.includes('pelvis') || path.includes('pelvic')) {
    return 'Pelvic';
  }
  if (
    path.includes('arm_')
    || path.includes('upperarm')
    || path.includes('shoulder')
    || path.includes('scapula')
    || path.includes('humerus')
  ) {
    return 'Upper Extremity';
  }
  if (
    path.includes('leg_')
    || path.includes('lowerleg')
    || path.includes('knee')
    || path.includes('ankle')
    || path.includes('foot')
  ) {
    return 'Lower Extremity';
  }
  if (path.includes('/head') || path.includes('_head_')) {
    return 'Head';
  }

  return null;
}

export interface SoapLevelSkeletonMetadata {
  noteRegion: string;
  level: string;
  regionId: number | null;
  partIndex: number | null;
}

/** Resolve SOAP table row labels from skeleton ids so Adjust and Review stay aligned. */
export function resolveSoapLevelFromSkeleton(
  skeleton: Skeleton | null | undefined,
  partId: number,
  maskId?: number | null,
  fallbackPartName?: string | null,
): SoapLevelSkeletonMetadata {
  const effectiveMaskId = normalizeSkeletonMaskId(maskId);
  const skeletonLookup =
    skeleton && partId > 0 ? findPartInSkeleton(skeleton, partId, effectiveMaskId) : null;
  const level = skeletonLookup?.partName ?? (fallbackPartName?.trim() || 'Unknown');

  const noteRegion =
    resolveNoteRegionForPlaylistItem(skeleton, {
      partId,
      maskId: effectiveMaskId,
      bodyPart: { partName: level },
    })
    ?? inferNoteRegionFromPart(level, skeletonLookup)
    ?? inferNoteRegionFromMaskFileName(skeletonLookup?.maskFileName)
    ?? 'Unknown';

  return {
    noteRegion,
    level,
    regionId: skeletonLookup?.regionId ?? null,
    partIndex: skeletonLookup?.partIndex ?? null,
  };
}

export function resolveNoteRegionForMask(
  mask: Pick<MaskItem, 'noteRegion' | 'partName' | 'bodyPart'> | null | undefined,
  skeletonLookup: SkeletonPartLookup | null | undefined,
): NoteRegion | null {
  const fromMask = mask?.noteRegion?.trim() || mask?.bodyPart?.noteRegion?.trim();
  if (fromMask && NOTE_REGION_OPTIONS.includes(fromMask as NoteRegion)) {
    return fromMask as NoteRegion;
  }

  const partName = mask?.partName ?? '';
  const inferred = inferNoteRegionFromPart(partName, skeletonLookup);
  if (inferred) {
    console.warn('[note-region] Using fallback inference for part', partName, '→', inferred);
    return inferred;
  }

  return null;
}

export interface PlaylistNoteRegionInput {
  partId?: number | null;
  maskId?: number | null;
  bodyPart?: { partName?: string | null; noteRegion?: string | null } | null;
}

export function resolveNoteRegionForPlaylistItem(
  skeleton: Skeleton | null | undefined,
  playlistItem: PlaylistNoteRegionInput,
): NoteRegion | null {
  const resolvedPartId = (playlistItem.partId ?? 0) > 0 ? playlistItem.partId! : 0;
  const maskId = normalizeSkeletonMaskId(playlistItem.maskId);
  const skeletonLookup =
    skeleton && resolvedPartId > 0
      ? findPartInSkeleton(skeleton, resolvedPartId, maskId)
      : null;

  return resolveNoteRegionForMask(
    {
      noteRegion: skeletonLookup?.noteRegion ?? playlistItem.bodyPart?.noteRegion ?? null,
      partName: playlistItem.bodyPart?.partName ?? skeletonLookup?.partName ?? '',
    },
    skeletonLookup,
  );
}

export function compareNoteRegions(left: string, right: string): number {
  const leftOrder = NOTE_REGION_SORT_ORDER[left] ?? 999;
  const rightOrder = NOTE_REGION_SORT_ORDER[right] ?? 999;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }
  return left.localeCompare(right);
}
